// src/components/SlotPickerSheet.tsx
import React, { useEffect, useState } from 'react'
import { atlas, ProviderProfession } from '../api'
import Spinner from '../components/Spinner'
import { detectTimeZone } from '../countryRules'

type Props = {
  open: boolean
  onClose: () => void
  specialistId: string
  specialistLabel?: string
  onPick: (slotISO: string) => void
  profession?: ProviderProfession
  autoCloseOnPick?: boolean
}

export default function SlotPickerSheet({
  open,
  onClose,
  specialistId,
  specialistLabel,
  onPick,
  profession = 'doctor',
  autoCloseOnPick = true,
}: Props) {
  const [loading, setLoading] = useState(false)
  const [slots, setSlots] = useState<string[]>([])
  const [err, setErr] = useState<string|null>(null)
  const displayTimeZone = React.useMemo(() => detectTimeZone(), [])

  useEffect(() => {
    if (!open) return
    let mounted = true
    setLoading(true); setErr(null)
    atlas.availabilitySlots(profession, specialistId, 30)
      .then(res => {
        if (!mounted) return
        setSlots((res?.slots ?? []) as string[])
      })
      .catch(e => setErr(e?.message || 'Failed to load availability'))
      .finally(() => setLoading(false))
    return () => { mounted = false }
  }, [open, specialistId, profession])

  return (
    <div className={`overlay ${open ? 'show' : ''}`} role="dialog" aria-modal="true" aria-label="Pick a slot">
      <div className="sheet">
        <div className="sheet-header">
          <div className="sheet-title">
            <span>Pick a slot</span>
            {specialistLabel && <span className="text-muted"> · {specialistLabel}</span>}
          </div>
          <button className="btn" onClick={onClose}>✕</button>
        </div>

        <div className="sheet-body">
          {loading && <Spinner label="Loading slots…" size={20} />}
          {err && <div className="banner error">{err}</div>}

          {!loading && !err && (
            <div className="inline-gap-2" style={{ flexWrap:'wrap' }}>
              {slots.length === 0 && <p className="text-sm text-gray-600">No upcoming slots.</p>}
              {slots.map(iso => (
                <button
                  key={iso}
                  className="slot-chip available"
                  onClick={() => {
                    onPick(iso)
                    if (autoCloseOnPick !== false) onClose()
                  }}
                >
                  {new Date(iso).toLocaleString('en-AU', { timeZone: displayTimeZone, hour12: false })}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="sheet-footer">
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
